import {
  QueryKey,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import { normalize } from 'normalizr';

import { NoteTemplate, VisitNoteTemplate } from 'EntityTypes';
import {
  AdminCopyTemplateRequestVariables,
  AdminCopyTemplateResponseBody,
  AppQueryConfig,
  CopyNoteTemplateRequestVariables,
  CopyNoteTemplateResponseBody,
  CreateNoteTemplateRequestVariables,
  CreateNoteTemplateResponseBody,
  DeleteNoteTemplateRequestVariables,
  ExportVN1TemplateToNote2RequestVariables,
  GetNoteTemplateResponseBody,
  GetNoteTemplateQueryParams,
  GetNoteTemplatesQueryParams,
  GetNoteTemplatesResponseBody,
  GetVisitNoteTemplatesResponseBody,
  UpdateNoteTemplateRequestVariables,
  UpdateNoteTemplateResponseBody,
} from 'QueryTypes';
import { EntitiesById } from 'StoreTypes';
import { getQueryUrl, jsonFetch } from 'utils/react-query';
import { makeUpdateFn } from 'utils/redux-query';

import { visitNoteTemplateSchema } from './visitNoteTemplatesSchemas';

type QueryConfigOptions = {
  ignoreWorkspace?: boolean;
};

const urls = {
  visitNoteTemplates(practiceId: number, options: QueryConfigOptions = {}): string {
    const { ignoreWorkspace } = options;
    const args = ignoreWorkspace ? `?ignore_workspace=${ignoreWorkspace}` : '';
    return `/practice/${practiceId}/visit-note-templates/${args}`;
  },
};

const queryKeys = {
  // Visit Note 1.0
  exportVisitNoteTemplate: (patientId: number, noteId: number, templateIds: number[]): QueryKey => [
    'patient',
    patientId,
    'visit-notes',
    noteId,
    'visit-note-templates',
    templateIds.join(';'),
  ],
  visitNoteTemplates: (practiceId: MaybeNullishId): QueryKey => [
    'practice',
    practiceId,
    'visit-note-templates',
  ],

  // Note 2.0
  adminCopyTemplates: ['gw', 'visit-notes-api', 'admin', 'copy-templates'],

  noteTemplates: (
    practiceId: MaybeNullishId,
    queryParams?: GetNoteTemplatesQueryParams,
  ): QueryKey => {
    // Only include query params if they are provided. This is to ensure that queries can
    // invalidate the base key and invalidate all queries with keys that have or don't have
    // query params.
    const baseKey = ['gw', 'visit-notes-api', 'practices', practiceId, 'note-templates'];
    return isEmpty(queryParams) ? baseKey : [...baseKey, queryParams];
  },

  noteTemplateCopy: (practiceId: number, noteTemplateId: number): QueryKey => [
    ...queryKeys.noteTemplateDetail(practiceId, noteTemplateId),
    'copy',
  ],

  noteTemplateDetail: (
    practiceId: MaybeNullishId,
    noteTemplateId: MaybeNullishId,
    queryParams?: GetNoteTemplateQueryParams,
  ): QueryKey => {
    const baseKey: QueryKey = [...queryKeys.noteTemplates(practiceId), noteTemplateId];
    return isEmpty(queryParams) ? baseKey : [...baseKey, queryParams];
  },
};

/**
 * Fetch visit note templates for the specified practice.
 *
 * FUTURE: deprecate in favor of `useGetVisitNoteTemplates`. Main blocker
 * right now is that this is being used by a few class components, which
 * can't use hooks.
 */
// eslint-disable-next-line import/prefer-default-export
export const visitNoteTemplatesQuery = (
  practiceId: number,
  options: QueryConfigOptions = {},
): AppQueryConfig => ({
  url: urls.visitNoteTemplates(practiceId, options),
  transform: (
    responseJson: VisitNoteTemplate[],
  ): { visitNoteTemplates: EntitiesById<VisitNoteTemplate> } =>
    normalize(responseJson, [visitNoteTemplateSchema]).entities,
  update: {
    visitNoteTemplates: makeUpdateFn<VisitNoteTemplate>({ merge: false }),
  },
});

/**
 * Fetches and returns the list of `VisitNoteTemplates` for the specified
 * practice. If the current user is in a workspace, this list will be
 * narrowed to just the templates in that workspace.
 */
export function useGetVisitNoteTemplates(
  practiceId: MaybeNullishId,
  options?: UseQueryOptions<GetVisitNoteTemplatesResponseBody>,
): UseQueryResult<GetVisitNoteTemplatesResponseBody> {
  return useQuery({
    queryKey: queryKeys.visitNoteTemplates(practiceId),
    ...options,
    enabled: Boolean(practiceId) && options?.enabled !== false,
  });
}

/**
 * Returns a mutation for exporting a list of visit note templates to a Note 2.0.
 */
export function useExportVisitNoteTemplate(): UseMutationResult<
  unknown,
  unknown,
  ExportVN1TemplateToNote2RequestVariables
> {
  return useMutation({
    mutationFn(variables) {
      const { noteId, patientId, templateIds } = variables;
      const url = getQueryUrl(queryKeys.exportVisitNoteTemplate(patientId, noteId, templateIds));
      return jsonFetch(url, { method: 'POST' });
    },
  });
}

/**
 * Fetches and returns the list of `NoteTemplate`s for the specified practice.
 */
export function useGetNoteTemplates(
  practiceId: MaybeNullishId,
  queryParams: GetNoteTemplatesQueryParams = {},
  queryOptions?: Omit<
    UseQueryOptions<GetNoteTemplatesResponseBody>,
    'queryKey' | 'queryFn' | 'meta'
  >,
): UseQueryResult<GetNoteTemplatesResponseBody> {
  return useQuery({
    queryKey: queryKeys.noteTemplates(practiceId, queryParams),
    ...queryOptions,
    enabled: Boolean(practiceId) && queryOptions?.enabled !== false,
    meta: { trailingSlashUrl: false },
  });
}

/**
 * Fetches and returns the specified `NoteTemplate`.
 */
export function useGetNoteTemplate(
  practiceId: MaybeNullishId,
  noteTemplateId: MaybeNullishId,
  queryParams?: GetNoteTemplateQueryParams,
): UseQueryResult<GetNoteTemplateResponseBody> {
  return useQuery({
    queryKey: queryKeys.noteTemplateDetail(practiceId, noteTemplateId, queryParams),
    enabled: Boolean(practiceId && noteTemplateId),
    meta: { trailingSlashUrl: false },
  });
}

/**
 * An imperative version of `useGetNoteTemplate`. Returns a function that can
 * be used to imperatively fetch the specified `NoteTemplate`.
 *
 * Only use this if you know you need imperative fetching and understand why
 * declarative fetching will NOT work for your use case; otherwise, use the
 * standard `useGetNoteTemplate` instead.
 */
export function useGetNoteTemplateImperative(): (
  practiceId: number,
  noteTemplateId: number,
  queryParams?: GetNoteTemplateQueryParams,
) => Promise<NoteTemplate> {
  const queryClient = useQueryClient();

  const getNoteTemplateImperative = async (
    practiceId: number,
    noteTemplateId: number,
    queryParams?: GetNoteTemplateQueryParams,
  ): Promise<NoteTemplate> => {
    const data = await queryClient.fetchQuery<GetNoteTemplateResponseBody>({
      queryKey: queryKeys.noteTemplateDetail(practiceId, noteTemplateId, queryParams),
      meta: { trailingSlashUrl: false },
    });

    return data;
  };

  return getNoteTemplateImperative;
}

/**
 * Returns a mutation to create a new `NoteTemplate`.
 */
export function useCreateNoteTemplate(): UseMutationResult<
  CreateNoteTemplateResponseBody,
  unknown,
  CreateNoteTemplateRequestVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn(variables) {
      const { body, practiceId } = variables;
      const url = getQueryUrl(queryKeys.noteTemplates(practiceId), { trailingSlashUrl: false });
      return jsonFetch(url, {
        body,
        method: 'POST',
      });
    },
    onSuccess(data, { practiceId }) {
      queryClient.invalidateQueries({ queryKey: queryKeys.noteTemplates(practiceId) });
      queryClient.setQueryData(queryKeys.noteTemplateDetail(practiceId, data.id), data);
    },
  });
}

/**
 * Returns a mutation to update the specified `NoteTemplate`.
 */
export function useUpdateNoteTemplate(): UseMutationResult<
  UpdateNoteTemplateResponseBody,
  unknown,
  UpdateNoteTemplateRequestVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn(variables) {
      const { body, practiceId, templateId } = variables;
      const url = getQueryUrl(queryKeys.noteTemplateDetail(practiceId, templateId), {
        trailingSlashUrl: false,
      });
      return jsonFetch(url, {
        body,
        method: 'PATCH',
      });
    },
    onSuccess(data, { practiceId, templateId }) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.noteTemplates(practiceId),
      });
      queryClient.setQueryData(queryKeys.noteTemplateDetail(practiceId, templateId), data);
    },
  });
}

/**
 * Returns a mutation to delete the specified `NoteTemplate`.
 */
export function useDeleteNoteTemplate(): UseMutationResult<
  unknown,
  unknown,
  DeleteNoteTemplateRequestVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn(variables) {
      const { practiceId, templateId } = variables;
      const url = getQueryUrl(queryKeys.noteTemplateDetail(practiceId, templateId), {
        trailingSlashUrl: false,
      });
      return jsonFetch(url, { method: 'DELETE' });
    },
    onSuccess(_data, { practiceId, templateId }) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.noteTemplates(practiceId),
      });
      queryClient.removeQueries(queryKeys.noteTemplateDetail(practiceId, templateId));
    },
  });
}

/**
 * Returns a mutation to copy the specified `NoteTemplate` within the same practice
 * (i.e. create a duplicate).
 */
export function useCopyNoteTemplate(): UseMutationResult<
  CopyNoteTemplateResponseBody,
  unknown,
  CopyNoteTemplateRequestVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn({ practiceId, templateId }) {
      const url = getQueryUrl(queryKeys.noteTemplateCopy(practiceId, templateId), {
        trailingSlashUrl: false,
      });
      return jsonFetch(url, {
        method: 'POST',
      });
    },
    onSuccess(data, { practiceId }) {
      queryClient.invalidateQueries(queryKeys.noteTemplates(practiceId));
      queryClient.setQueryData<GetNoteTemplateResponseBody>(
        queryKeys.noteTemplateDetail(practiceId, data.id),
        data,
      );
    },
  });
}

/**
 * Returns a mutation for an admin to copy the specified templates to a destination practice.
 */
export function useAdminCopyTemplate(): UseMutationResult<
  AdminCopyTemplateResponseBody,
  unknown,
  AdminCopyTemplateRequestVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn(variables) {
      const url = getQueryUrl(queryKeys.adminCopyTemplates, {
        trailingSlashUrl: false,
      });
      return jsonFetch(url, {
        body: variables,
        method: 'POST',
      });
    },
    onSuccess(_response, request) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.noteTemplates(request.target_practice_id),
      });
    },
  });
}
